"use client";

import React, { useState } from "react";
import { CircleCheck as CheckCircle2, FileText, CircleQuestionMark as HelpCircle, House as Home, Layers, Palette, ArrowRight, ShieldCheck, Building2, Clock, Compass } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface GuidanceTopic {
  id: string;
  tabLabel: string;
  title: string;
  subtitle: string;
  description: string;
  bullets: string[];
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
  highlightTitle: string;
  highlightPoints: {
    icon: any;
    title: string;
    text: string;
  }[];
}
const guidanceData: GuidanceTopic[] = [{
  id: "priorities",
  tabLabel: "1. Priorities & Must-Haves",
  title: "Setting Priorities and Must-Haves",
  subtitle: "Identify what matters most before drawing floor plans or choosing finishes.",
  description: "A successful renovation begins with honest assessment. Distinguish functional non-negotiables—such as kitchen work zones, structural flow, or plumbing upgrades—from aesthetic preferences that can adapt to spatial and financial boundaries.",
  bullets: ["Catalog everyday pain points in your current space rather than focusing solely on Pinterest inspirations.", "Categorize features into essential function, enhanced comfort, and decorative finish tiers.", "Plan for daily household routines over the next five to ten years rather than immediate trends."],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_planning_blueprints.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_planning_blueprints.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_planning_blueprints.png-wvc-sizes",
    alt: "Homeowners and designer reviewing floor plan priorities on an island counter"
  },
  highlightTitle: "Key Decision Filters",
  highlightPoints: [{
    icon: Compass,
    title: "Function First",
    text: "Traffic patterns, workflow, and storage volume outlast finish trends."
  }, {
    icon: Layers,
    title: "Mechanical Health",
    text: "Allocate adequate focus to behind-the-wall electrical, plumbing, and HVAC routing."
  }]
}, {
  id: "permits",
  tabLabel: "2. Brookhaven & DeKalb Permitting",
  title: "Understanding City of Brookhaven & DeKalb County Permitting",
  subtitle: "Navigate zoning, building review, and jurisdictional inspections cleanly.",
  description: "Renovations involving structural changes, mechanical re-routing, additions, or major footprint shifts require plan review and permits through the City of Brookhaven Community Development Department or DeKalb County authorities. Early coordination prevents costly project halts.",
  bullets: ["Verify whether your parcel falls under Brookhaven municipal codes or unincorporated DeKalb County jurisdiction.", "Structural alterations, load-bearing removals, and trade rough-ins require engineered drawings and official inspections.", "Tree canopy protection ordinances and impervious surface coverage caps strictly apply in Brookhaven residential zones."],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_architectural_plans.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_architectural_plans.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_architectural_plans.webp-wvc-sizes",
    alt: "Architectural blueprints and technical documents for residential plan review"
  },
  highlightTitle: "Permit Milestones",
  highlightPoints: [{
    icon: Building2,
    title: "Plan Review Submittal",
    text: "Site plans, framing schematics, and trade diagrams are reviewed for local code compliance."
  }, {
    icon: ShieldCheck,
    title: "Rough-in & Final Inspections",
    text: "Framing, plumbing, electrical, and insulation are inspected before drywall closes."
  }]
}, {
  id: "materials",
  tabLabel: "3. Design & Material Selection",
  title: "Preparing for Design and Material Selection",
  subtitle: "Proactively manage custom lead times, order sequences, and material deliveries.",
  description: "Cabinetry, custom tile, plumbing valves, and specialty windows frequently carry substantial fabrication cycles. Commencing physical demolition before all critical mechanical fixtures and custom items are on hand risks unnecessary delays.",
  bullets: ["Select rough-in plumbing trim and concealed valve valves alongside finish fixtures to prevent framing halts.", "Finalize cabinetry dimensions, appliance specifications, and electrical schematics simultaneously.", "Order surplus tile and stone yardage to account for patterned cuts, pattern layout, and potential dye-lot variation."],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/material_finish_samples.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/material_finish_samples.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/material_finish_samples.png-wvc-sizes",
    alt: "Material sample palette with natural oak, marble tile, and brushed brass fixtures"
  },
  highlightTitle: "Procurement Strategy",
  highlightPoints: [{
    icon: Clock,
    title: "Critical Path Ordering",
    text: "Stage long-lead custom cabinetry and structural doors early in the pre-construction phase."
  }, {
    icon: Palette,
    title: "Spec Sheets Verified",
    text: "Cross-examine appliance cut sheets against rough plumbing and dedicated electrical specs."
  }]
}, {
  id: "living",
  tabLabel: "4. Living During Construction",
  title: "Living Arrangements During Construction",
  subtitle: "Protect your family's routine, contain dust, and establish secure staging zones.",
  description: "Whether you decide to stay on-site during a kitchen overhaul or relocate for a whole-home overhaul, careful physical segregation of the job site reduces daily friction and keeps living quarters clean and safe.",
  bullets: ["Set up a temporary kitchenette with a refrigerator, microwave, and wash station outside the work zone.", "Establish strict dust barrier protection, zippered airlock entries, and negative-pressure HEPA filtration.", "Designate dedicated routes for trade entry, debris containment, and daily tool unloading."],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_dust_barrier_home.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_dust_barrier_home.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeling_dust_barrier_home.png-wvc-sizes",
    alt: "Neatly taped floor protection and clear zippered dust barrier partition in residential hallway"
  },
  highlightTitle: "Site Setup Essentials",
  highlightPoints: [{
    icon: Home,
    title: "Safe Living Quarters",
    text: "Secure bedrooms and pet areas with mechanical air isolations and protected floor paths."
  }, {
    icon: FileText,
    title: "Daily Communication",
    text: "Define working hours, parking guidelines, and site access routines with your project lead."
  }]
}, {
  id: "questions",
  tabLabel: "5. Questions to Ask a Contractor",
  title: "Questions to Ask a Contractor",
  subtitle: "Inquire deeply into project supervision, site protocols, and change procedures.",
  description: "An informed interview process goes far beyond baseline credentials. Understanding how a builder handles day-to-day site supervision, unexpected site discoveries, and trade quality control protects your home and peace of mind.",
  bullets: ["Who will be physically on-site managing daily trade work, and how frequently do we receive progress updates?", "What is your written procedure when structural or mechanical hidden conditions are uncovered behind walls?", "How do you protect finished surfaces, control dust migration, and maintain daily broom-clean site discipline?", "Can you walk us through how trade payment milestones correspond to completed work and municipal sign-offs?"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/contractor_client_consultation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/contractor_client_consultation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/contractor_client_consultation.png-wvc-sizes",
    alt: "General contractor explaining wall framing and project milestones to homeowners on site"
  },
  highlightTitle: "Interview Pillars",
  highlightPoints: [{
    icon: HelpCircle,
    title: "On-Site Management",
    text: "Ensure you know the direct phone number and presence schedule of your on-site project coordinator."
  }, {
    icon: CheckCircle2,
    title: "Change Order Protocol",
    text: "Require that all modifications to scope receive written cost and schedule confirmation before execution."
  }]
}];
export default function RenovationGuidance() {
  const [activeTab, setActiveTab] = useState("priorities");
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="renovationguidance" data-nav="light" ref={ref} style={{
    opacity: isVisible ? 1 : 0
  }} className="bg-background text-foreground py-20 md:py-28 transition-opacity duration-700 ease-out">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <p className="text-primary font-medium text-sm tracking-wider uppercase mb-2">
            Homeowner Planning Manual
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground mb-4">
            Five essential pillars for a stress-free renovation
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Thorough preparation safeguards your schedule, minimizes disruption, and ensures every detail
            aligns with your household needs. Review our local guidance before swinging a hammer.
          </p>
        </div>

        {/* Interactive Tabs Layout */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-8">
          {/* Scrollable Tab Controls */}
          <div className="overflow-x-auto pb-2 scrollbar-none border-b border-border">
            <TabsList className="bg-transparent h-auto p-0 flex gap-2 sm:gap-3 min-w-max justify-start rounded-none">
              {guidanceData.map((item, i) => <TabsTrigger key={item.id} data-index={i} value={item.id} className="rounded-t-lg rounded-b-none border-b-2 border-transparent px-4 py-3 text-sm font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-card data-[state=active]:text-foreground shadow-none transition-all">
                  {item.tabLabel}
                </TabsTrigger>)}
            </TabsList>
          </div>

          {/* Tab Content Panels */}
          {guidanceData.map((item, index) => <TabsContent key={item.id} data-index={index} value={item.id} className="mt-0 focus-visible:outline-none">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
                {/* Left Column: Guidance Details */}
                <div className="lg:col-span-7 flex flex-col gap-6">
                  <div>
                    <span className="text-xs uppercase tracking-widest text-primary font-semibold">
                      Step {index + 1} of 5
                    </span>
                    <h3 className="font-serif text-2xl sm:text-3xl font-medium text-foreground mt-1 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm sm:text-base font-medium text-foreground/80 mb-4">
                      {item.subtitle}
                    </p>
                    <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
                      {item.description}
                    </p>
                  </div>

                  {/* Bullet Checklist */}
                  <div className="bg-card border border-border rounded-xl p-5 sm:p-6 shadow-xs">
                    <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-4">
                      Action Checklist
                    </h4>
                    <ul className="space-y-3">
                      {item.bullets.map((bullet, bIdx) => <li key={bIdx} data-index={bIdx} className="flex items-start gap-3 text-sm text-foreground/90 leading-relaxed">
                          <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                          <span>{bullet}</span>
                        </li>)}
                    </ul>
                  </div>

                  {/* Highlight Badges / Sub-points */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {item.highlightPoints.map((pt, pIdx) => {
                  const IconComponent = pt.icon;
                  return <Card key={pIdx} data-index={pIdx} className="bg-card/70 border-border rounded-lg shadow-2xs py-4">
                          <CardContent className="px-4 py-0 flex flex-col gap-2">
                            <div className="flex items-center gap-2 text-foreground font-medium text-sm">
                              <div className="p-1.5 rounded-md bg-primary/10 text-primary">
                                <IconComponent className="size-4" />
                              </div>
                              {pt.title}
                            </div>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {pt.text}
                            </p>
                          </CardContent>
                        </Card>;
                })}
                  </div>
                </div>

                {/* Right Column: Photography & Contact Reference */}
                <div className="lg:col-span-5 flex flex-col gap-6">
                  <div className="relative rounded-2xl overflow-hidden border border-border shadow-md bg-muted aspect-4/3 sm:aspect-16/11">
                    <img src={item.image.src} data-wvc-srcset={item.image.srcset} data-wvc-sizes={item.image.sizes} alt={item.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-103" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
                    <div className="absolute bottom-3 left-4 right-4 text-white text-xs font-light tracking-wide drop-shadow-xs">
                      {item.highlightTitle}
                    </div>
                  </div>

                  <Card className="bg-secondary text-secondary-foreground border-none shadow-md rounded-xl py-6">
                    <CardContent className="px-6 py-0 flex flex-col gap-4">
                      <div>
                        <h4 className="font-serif text-lg font-medium text-secondary-foreground">
                          Ready to discuss your project?
                        </h4>
                        <p className="text-xs sm:text-sm text-secondary-foreground/80 mt-1">
                          We will follow up to discuss your project, handling architectural design, municipal permitting, and full site supervision.
                        </p>
                      </div>
                      <div>
                        <Button asChild variant="default" size="sm" className="w-full sm:w-auto font-medium">
                          <Link to="/ContactRequestAnEstimatePage">
                            Request an Estimate
                            <ArrowRight className="size-4 ml-1" />
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>)}
        </Tabs>
      </div>
    </section>;
}